import {Component, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {FormArray, FormBuilder, Validators} from '@angular/forms';
import {filter, switchMap, take} from 'rxjs/operators';
import {from} from 'rxjs';
import firebase from 'firebase/compat/app';
import {Term, TermContent} from '../../models/term';
import {DomSanitizer, SafeHtml} from '@angular/platform-browser';
import {CompanyService} from '../../services/company.service';
import {AuthService} from '../../services/auth.service';
import {FeatureFlagsService} from '../../services/feature-flags.service';

export type TermType = 'complete' | 'simple';

@UntilDestroy()
@Component({
  selector: 'app-terms',
  templateUrl: './terms.component.html',
  styleUrls: ['./terms.component.scss']
})
export class TermsComponent implements OnInit {
  term: Term;
  termControls: FormArray;
  type: TermType;
  buttonLabel = 'Próximo';
  showDeadlineWarning = false;

  constructor(
    private activatedRoute: ActivatedRoute,
    private fb: FormBuilder,
    private companyService: CompanyService,
    private router: Router,
    private sanitizer: DomSanitizer,
    private authService: AuthService,
    private featureFlagsService: FeatureFlagsService,
  ) {}

  get content(): TermContent {
    return this.type === 'complete' ? this.term?.content?.complete : this.term?.content?.simple;
  }

  get contentBody(): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(this.content?.body);
  }

  ngOnInit(): void {
    this.termControls = this.fb.array([]);

    this.activatedRoute.data
      .pipe(untilDestroyed(this))
      .subscribe((data: {term: Term, type: TermType}) => {
        this.term = data.term;
        this.type = data.type;
        if (this.type === 'complete') {
          this.buttonLabel = 'Fechar';
        } else {
          this.term.conditions.forEach(() => {
            this.termControls.push(this.fb.control(false, [Validators.requiredTrue]));
          });
        }
      });

    this.checkOrganizationWarning();
  }

  private checkOrganizationWarning(): void {
    this.authService.user$
      .pipe(
        take(1),
        filter((user): user is firebase.User => !!user),
        switchMap((user) => from(user.getIdTokenResult())),
        untilDestroyed(this)
      )
      .subscribe((token) => {
        const companyId = token?.claims?.['company_id'];
        const allowedOrgs = this.featureFlagsService.featureFlags.termsWarning;

        if (!companyId) {
          return;
        }

        if (typeof allowedOrgs === 'string' && allowedOrgs.length > 0) {
          this.showDeadlineWarning = allowedOrgs.split(',').map((id) => id.trim()).includes(String(companyId));
        }
      });
  }

  saveOrClose(): void {
    if (this.type === 'complete') {
      this.router.navigate(['/s/termos'], {replaceUrl: true}); // go back to simple terms
    } else {
      this.companyService.acceptTerms(this.term);
      this.router.navigate(['/s/documento'], {replaceUrl: true});
    }
  }

  showCompleteRules(): void {
    this.router.navigate(['/s/termos/completo']);
  }
}
